import { useCallback, useEffect } from "react";

import { useKpiDatasetKind } from "./useKpiDatasetKind";
import type {
  XFactorKpiColumn,
  XFactorKpiRowModel,
} from "../Pages/TempViewsRendering/lib/deriveKpi";
import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  fetchKpiMatrix,
  kpiMatrixKey,
  type XFactorKpiMatrixGranularity,
} from "../Store/Reducers/XFactorKpiSlice";

export type { XFactorKpiMatrixGranularity };

export type XFactorKpiMatrixState = {
  columns: XFactorKpiColumn[];
  rowsByAccountId: Record<string, XFactorKpiRowModel>;
  date: string | null;
  loading: boolean;
  error: string | null;
  /** Re-runs the same fetch — call after a successful /kpi/generate so a
   * newly-generated row's data appears without a full page reload. */
  refresh: () => void;
};

const EMPTY_COLUMNS: XFactorKpiColumn[] = [];
const EMPTY_ROWS: Record<string, XFactorKpiRowModel> = {};

/**
 * Portfolio-wide KPI matrix rows (render/kpi view=table), one call for every
 * account. Additive alongside Hawkeye's own matrix data hook — this is a new,
 * separate data source, not a change to hawkeyeMetricsCore.ts.
 *
 * `measurementDate` is an exact-date override; `granularity`+`period` narrow
 * resolution to a period window instead (e.g. a specific month) and are
 * ignored when `measurementDate` is set. Omit all three for xfactor's own
 * newest-briefed-date resolution per account. Data lives in XFactorKpiSlice.
 */
export const useXFactorKpiMatrix = (
  accountIds: string[],
  measurementDate?: string | null,
  granularity?: XFactorKpiMatrixGranularity | null,
  period?: string | null
): XFactorKpiMatrixState => {
  const dispatch = useAppDispatch();
  const datasetKind = useKpiDatasetKind();
  const idsKey = accountIds.join(",");
  const key = accountIds.length
    ? kpiMatrixKey({
        accountIds,
        datasetKind,
        measurementDate,
        granularity,
        period,
      })
    : null;
  const entry = useAppSelector(state =>
    key ? state.xfactorKpi.matrices[key] : undefined
  );

  const load = useCallback(() => {
    if (!accountIds.length) return;
    dispatch(
      fetchKpiMatrix({
        accountIds,
        datasetKind,
        measurementDate,
        granularity,
        period,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, idsKey, measurementDate, granularity, period, datasetKind]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    columns: entry?.columns ?? EMPTY_COLUMNS,
    rowsByAccountId: entry?.rowsByAccountId ?? EMPTY_ROWS,
    date: entry?.date ?? null,
    loading: entry ? entry.loading : accountIds.length > 0,
    error: entry?.error ?? null,
    refresh: load,
  };
};

export default useXFactorKpiMatrix;
